import React from 'react';
import { Card, CardBody } from '@nextui-org/react';

const TEAL = '#3fced1';

export interface LeaderboardRow {
  name?: string | null;
  image?: string | null;
  value?: number | string | null;
  sublabel?: string | null;
}

interface LeaderboardTableProps {
  title: string;
  icon?: React.ReactNode;
  rows: LeaderboardRow[];
  valueLabel?: string;
  /** Werte als Dezimalzahl formatieren (z.B. Averages) */
  decimals?: number;
  onPlayerClick?: (name: string) => void;
  emptyText?: string;
}

function toNum(value: number | string | null | undefined): number {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const n = parseFloat(value.replace(',', '.'));
    if (!isNaN(n)) return n;
  }
  return 0;
}

/**
 * Leaderboard-Tabelle mit Tailwind-Balken (relativ zum Maximalwert).
 * Keine externen Chart-Dependencies.
 */
const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  title,
  icon,
  rows,
  valueLabel,
  decimals,
  onPlayerClick,
  emptyText = 'Keine Daten verfügbar',
}) => {
  const safeRows = Array.isArray(rows) ? rows.filter((r) => r && r.name) : [];
  const max = safeRows.reduce((m, r) => Math.max(m, toNum(r.value)), 0);

  return (
    <Card className="bg-zinc-900 border border-zinc-800">
      <CardBody className="p-0">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            {icon && <span style={{ color: TEAL }}>{icon}</span>}
            {title}
          </h3>
          {valueLabel && (
            <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
              {valueLabel}
            </span>
          )}
        </div>

        {safeRows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">{emptyText}</p>
        ) : (
          <ul className="divide-y divide-zinc-800/60">
            {safeRows.map((row, idx) => {
              const num = toNum(row.value);
              const pct = max > 0 ? Math.max(2, Math.round((num / max) * 100)) : 0;
              const display =
                typeof decimals === 'number' ? num.toFixed(decimals) : num.toLocaleString('de-DE');
              const name = row.name || 'Unbekannt';
              return (
                <li key={`${name}-${idx}`} className="px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-5 text-right text-xs font-bold tabular-nums flex-shrink-0 ${
                        idx === 0 ? '' : 'text-gray-500'
                      }`}
                      style={idx === 0 ? { color: TEAL } : undefined}
                    >
                      {idx + 1}
                    </span>
                    {row.image ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img
                        src={row.image}
                        alt={name}
                        className="w-7 h-7 rounded-full object-cover bg-zinc-800 flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] text-gray-500 flex-shrink-0">
                        {name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        {onPlayerClick ? (
                          <button
                            onClick={() => onPlayerClick(name)}
                            className="text-sm text-left truncate hover:underline transition-colors"
                            style={{ color: '#e4e4e7' }}
                            onMouseEnter={(e) => ((e.target as HTMLElement).style.color = TEAL)}
                            onMouseLeave={(e) => ((e.target as HTMLElement).style.color = '#e4e4e7')}
                          >
                            {name}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-200 truncate">{name}</span>
                        )}
                        <span className="text-sm font-semibold tabular-nums flex-shrink-0">
                          {display}
                        </span>
                      </div>
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: TEAL, opacity: idx === 0 ? 1 : 0.55 }}
                        />
                      </div>
                      {row.sublabel && (
                        <p className="text-[10px] text-gray-500 mt-0.5 truncate">{row.sublabel}</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

export default LeaderboardTable;
